// Maps UserServiceClientError codes to user-facing copy. Pure function, no
// I/O — easy to unit test and reuse across signup + verify-email screens.
import {
  UserServiceClientError,
  type UserServiceClientErrorCode,
} from "@its-engineering-and-development/user-service-client";

export type MappedUserServiceError = {
  code: UserServiceClientErrorCode | "UNKNOWN";
  message: string;
};

type AdditionalUserServiceErrorCode =
  | "INVALID_CREDENTIALS"
  | "INVALID_PASSWORD"
  | "PASSWORD_RESET_REQUIRED";

type SupportedUserServiceErrorCode =
  | UserServiceClientErrorCode
  | AdditionalUserServiceErrorCode;

const MESSAGES = {
  USER_EXISTS: "An account with this email already exists.",
  USER_NOT_FOUND: "We couldn't find an account for that email.",
  USER_NOT_CONFIRMED: "This account hasn't been verified yet.",
  INVALID_CREDENTIALS: "The email or password you entered is incorrect.",
  INVALID_PASSWORD:
    "Your password must include uppercase, lowercase, number, and symbol characters.",
  PASSWORD_RESET_REQUIRED:
    "Your password needs to be reset before you can continue.",
  INVALID_CODE: "That code is incorrect. Check the email and try again.",
  EXPIRED_CODE: "That code has expired. Click resend to get a new one.",
  INVALID_PARAMETER: "Some of the information you entered isn't valid.",
  RATE_LIMITED: "Too many attempts. Please wait a moment and try again.",
  UPSTREAM_FAILURE:
    "The sign-up service is temporarily unavailable. Please try again.",
  NETWORK_ERROR:
    "Can't reach the sign-up service. Check your connection and try again.",
  CANCELLED: "The request was cancelled.",
  UNKNOWN: "Something went wrong. Please try again.",
} satisfies Record<SupportedUserServiceErrorCode, string>;

export function mapUserServiceError(
  error: unknown,
  fallback = MESSAGES.UNKNOWN,
): MappedUserServiceError {
  if (error instanceof UserServiceClientError) {
    return { code: error.code, message: MESSAGES[error.code] ?? fallback };
  }
  return { code: "UNKNOWN", message: fallback };
}
